import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { theme } from '@ui/theme';
import { AnimatedRingPath } from './AnimatedRingPath.js';
import { RollingNumber } from './RollingNumber.js';
import {
  Wrapper,
  StyledStatRingOverlay,
  MobileSVG,
  DesktopSVG,
  StatName,
  RingContainer,
  ScoreContainer,
  PillText,
} from './styles.js';

export const StatRingOverlay = ({
  statName,
  score = 100,
  pillText,
  pillColor = 'text.lightest',
  circleFillColor = 'status.successPrimary',
  circleBackgroundColor = 'background.lightest',
  mobileAlignment = 'left',
  desktopAlignment = 'left',
}) => {
  const containerRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const cappedScore = Math.min(score, 100);
  const duration = Math.min(1 + Math.max(cappedScore - 10, 0) * 0.02, 3);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <Wrapper
      $mobileAlignment={mobileAlignment}
      $desktopAlignment={desktopAlignment}
    >
      <StyledStatRingOverlay ref={containerRef}>
        <RingContainer>
          <MobileSVG>
            <svg
              width="122"
              height="100"
              viewBox="0 0 122 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d={
                  'M14.8971 92.9729C8.96706 84.504 5.49451 74.5602 4.86335 64.2408C4.23219 53.9214 6.467 43.6285 11.3207 34.5C16.1744 25.3715 23.4579 17.7631 32.366 12.5158C41.2741 7.26855 51.4597 4.58689 61.7968 4.76733C72.1339 4.94776 82.2197 7.98325 90.9393 13.5382C99.6588 19.0932 106.672 26.9512 111.205 36.2435C115.737 45.5358 117.611 55.9004 116.62 66.1915C115.629 76.4826 111.812 86.2991 105.59 94.556'
                }
                stroke={theme.colors.get(circleBackgroundColor)}
                strokeWidth="9.51777"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <AnimatedRingPath
                d={
                  'M14.8971 92.9729C8.96706 84.504 5.49451 74.5602 4.86335 64.2408C4.23219 53.9214 6.467 43.6285 11.3207 34.5C16.1744 25.3715 23.4579 17.7631 32.366 12.5158C41.2741 7.26855 51.4597 4.58689 61.7968 4.76733C72.1339 4.94776 82.2197 7.98325 90.9393 13.5382C99.6588 19.0932 106.672 26.9512 111.205 36.2435C115.737 45.5358 117.611 55.9004 116.62 66.1915C115.629 76.4826 111.812 86.2991 105.59 94.556'
                }
                strokeWidth="9.51777"
                fillColor={circleFillColor}
                score={cappedScore}
                isInView={isInView}
                duration={duration}
              />
            </svg>
          </MobileSVG>
          <DesktopSVG>
            <svg
              viewBox="0 0 284 232"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d={
                  'M34.7605 216.937C20.9238 197.176 12.8212 173.974 11.3485 149.895C9.87577 125.817 15.0903 101.8 26.4156 80.5002C37.7409 59.2004 54.7358 41.4473 75.5213 29.2037C96.3069 16.9601 120.073 10.7029 144.193 11.1239C168.313 11.5449 191.847 18.6278 212.192 31.5893C232.538 44.5509 248.903 62.8862 259.478 84.5683C270.053 106.25 274.426 130.435 272.114 154.447C269.802 178.459 260.894 201.365 246.377 220.631'
                }
                stroke={theme.colors.get(circleBackgroundColor)}
                strokeWidth="22.2081"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <AnimatedRingPath
                d={
                  'M34.7605 216.937C20.9238 197.176 12.8212 173.974 11.3485 149.895C9.87577 125.817 15.0903 101.8 26.4156 80.5002C37.7409 59.2004 54.7358 41.4473 75.5213 29.2037C96.3069 16.9601 120.073 10.7029 144.193 11.1239C168.313 11.5449 191.847 18.6278 212.192 31.5893C232.538 44.5509 248.903 62.8862 259.478 84.5683C270.053 106.25 274.426 130.435 272.114 154.447C269.802 178.459 260.894 201.365 246.377 220.631'
                }
                strokeWidth="22.2081"
                fillColor={circleFillColor}
                score={cappedScore}
                isInView={isInView}
                duration={duration}
              />
            </svg>
          </DesktopSVG>
          <ScoreContainer>
            <RollingNumber value={cappedScore} isInView={isInView} />
            <PillText $color={circleFillColor} $fontColor={pillColor}>
              {pillText}
            </PillText>
          </ScoreContainer>
        </RingContainer>
        {statName && <StatName>{statName}</StatName>}
      </StyledStatRingOverlay>
    </Wrapper>
  );
};

StatRingOverlay.displayName = 'StatRingOverlay';

StatRingOverlay.propTypes = {
  statName: PropTypes.string,
  score: PropTypes.number,
  pillText: PropTypes.string,
  pillColor: PropTypes.string,
  circleFillColor: PropTypes.string,
  mobileAlignment: PropTypes.string,
  desktopAlignment: PropTypes.string,
};
